import { useState } from 'react';
import type { ReportSource, FieldReference } from '../api/types';

interface ReportFieldTreeProps {
  source: ReportSource;
  allSources: ReportSource[];
  path: string[];
  depth: number;
  selectedFields: FieldReference[];
  onToggleField: (fieldRef: FieldReference) => void;
}

export function ReportFieldTree({
  source,
  allSources,
  path,
  depth,
  selectedFields,
  onToggleField,
}: ReportFieldTreeProps) {
  const [expandedRelations, setExpandedRelations] = useState<string[]>([]);

  const toggleExpand = (relationPath: string) => {
    setExpandedRelations((prev) =>
      prev.includes(relationPath)
        ? prev.filter((r) => r !== relationPath)
        : [...prev, relationPath]
    );
  };

  const isSelected = (fieldName: string) => {
    return selectedFields.some(
      (sf) =>
        sf.field === fieldName &&
        (sf.path || []).join('.') === path.join('.')
    );
  };

  return (
    <div className={`mt-2 ${depth > 0 ? 'pr-4 border-r-2 border-gray-200 mr-2' : ''}`}>
      {depth > 0 && <div className="text-xs font-semibold text-gray-500 mb-2">فیلدهای {source.verbose_name}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
        {source.fields.map((f) => (
          <label 
            key={f} 
            dir="rtl" 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              padding: '6px 12px',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <input
              type="checkbox"
              style={{ width: '16px', height: '16px', margin: 0 }}
              checked={isSelected(f)}
              onChange={() =>
                onToggleField({
                  path: path.length > 0 ? path : undefined,
                  field: f,
                })
              }
            />
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
              {source.field_labels?.[f] || f.replace(/__/g, " - ").replace(/_/g, " ")}
            </span>
          </label>
        ))}
      </div>

      {source.related_sources && source.related_sources.length > 0 && depth < 3 && (
        <div className="mt-4 border-t pt-2 border-gray-100">
          <div className="text-xs font-semibold text-blue-600 mb-2">رابطه‌های مجاز (JOIN)</div>
          {source.related_sources.map((rel) => {
            const isExpanded = expandedRelations.includes(rel.relation_path);
            const targetSource = allSources.find((s) => s.key === rel.target_source_key);
            
            if (!targetSource) return null;
            
            return (
              <div key={rel.relation_path} className="mb-2">
                <button
                  type="button"
                  onClick={() => toggleExpand(rel.relation_path)}
                  className="text-sm font-medium text-blue-700 flex items-center gap-1 hover:text-blue-900 transition-colors"
                >
                  {isExpanded ? '▼' : '◀'} {rel.label} 
                </button>
                {isExpanded && (
                  <ReportFieldTree
                    source={targetSource}
                    allSources={allSources}
                    path={[...path, rel.relation_path]}
                    depth={depth + 1}
                    selectedFields={selectedFields}
                    onToggleField={onToggleField}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
